/**
 * Traits permanents par genre — **avenant A-4 décision 2**, complétée et validée le 14/08.
 *
 * Ce ne sont **pas** des jauges de capacité genrées : l'avenant l'interdit explicitement
 * (« JAMAIS de jauge de capacité genrée type charisme »). Ce sont des **modificateurs
 * d'environnement** — ce que la plateforme fait au créateur, pas ce que le créateur vaut.
 * Deux de chaque côté, un avantage et un coût :
 *
 * | Côté | Avantage | Coût |
 * |---|---|---|
 * | **créatrice** (`f`) | gains de **Fidélité ×1,2** — « la communauté-maison » | commentaires **+50 % plus durs** — « le Fil est plus rude » ⟶ **vit dans `fil.ts`** depuis M3.21 |
 * | **créateur** (`h`) | gains de **Réputation ×1,2** — « le préjugé favorable » | remontées de **SM hors sommeil ×0,8** — « personne ne s'inquiète » |
 *
 * **Trois règles que le code doit tenir, et qu'un relecteur doit pouvoir vérifier d'un œil :**
 *
 * 1. **Les GAINS seulement.** Un ×1,2 sur les pertes punirait le côté qu'il est censé
 *    avantager — une créatrice perdrait 20 % de Fidélité en plus à chaque bad buzz. Les
 *    deltas négatifs passent intacts.
 * 2. **Le sommeil est intact des deux côtés** (mot de l'avenant). Le ×0,8 ne touche que les
 *    remontées de SM **hors sommeil** : thérapie, pause, événements. Sans cette exception, un
 *    créateur ne récupérerait jamais et le trait deviendrait une condamnation, pas un coût.
 * 3. **`genre: null` ⇒ aucun trait.** C'est l'état des parties d'avant l'avenant et de tous
 *    les tests qui ne parlent pas de genre — dont les **ancres de calibrage**, que ces
 *    multiplicateurs fausseraient.
 *
 * ── Où le trait est branché ⟦A-26⟧ ─────────────────────────────────────────────
 *
 * Jusqu'au 16/08, `appliquerTraitJauge` n'avait **qu'un seul appelant** (`effects.ts`, les
 * `gauge_delta` de contenu) : la créatrice payait son ×1,5 de `lire_commentaires` à chaque
 * lecture et n'encaissait son ×1,2 que sur des événements rares — **le trait censé
 * l'avantager la pénalisait** (constat B4 d'`AUDIT-FORMULES.md`). La liste ci-dessous est
 * l'inventaire complet des points d'écriture, et elle se relit comme une preuve.
 *
 * | passe par le trait | pourquoi |
 * |---|---|
 * | `effects.ts` — `gauge_delta` de contenu | gains d'événements et de jalons |
 * | `publish.ts` — Fidélité +3 (longue réussie), +1 (sujet lumière) | les 2 sources de Fidélité de l'arc 1 |
 * | `pause.ts` — `RETOUR_FIDELITE`, `RETOUR_FIDELITE_EN_FORME` | gains ⟦A-2⟧ |
 * | `actions.ts` — Fidélité +2 (`menage`) | gain d'action |
 * | `actions.ts` — table §2.2, `mental_delta` **positif** hors `sleep` | thérapie +15, pause +40 |
 * | `gameover.ts` — Réputation restituée par des excuses réussies | le seul gain de Réputation du moteur |
 *
 * | reste intact | pourquoi |
 * |---|---|
 * | toutes les PERTES (flop, shorts en excès, érosion, `ABUS_FIDELITE`, crunch, dépassement) | règle 1 |
 * | `sleep.ts` — supplément de pause annoncée, `sleep_recovery_add`, consommables de nuit | règle 2 |
 * | `repos.ts` — `sm_nuits_contenues` | ce sont des NUITS : règle 2 |
 * | `gameover.ts` — `CRISE_SM_SORTIE`, état de reprise | des **assignations** de filet, pas des gains |
 *
 * ✎ Les valeurs (1,2 / 0,8 / +50 %) sont à valider par `simulate.ts` — **cible de l'avenant :
 * aucun genre objectivement plus fort sur 100 runs**. Tant que M3.12 n'a pas tranché, ce sont
 * des ordres de grandeur, pas des constantes acquises.
 */
/** Gains de Fidélité de la créatrice — « la communauté-maison ». ✎ */
export const MULT_FIDELITE_F = 1.2;
/** Gains de Réputation du créateur — « le préjugé favorable ». ✎ */
export const MULT_REPUTATION_H = 1.2;
/** Remontées de SM **hors sommeil** du créateur — « personne ne s'inquiète ». ✎ */
export const MULT_SM_HORS_SOMMEIL_H = 0.8;
/**
 * ⟦A-4 §2 · SPEC-FIL §7 · M3.21⟧ 🔴 **« Le Fil est plus rude » n'habite plus ici.**
 *
 * Le quatrième trait — +50 % de commentaires durs pour la créatrice — a vécu jusqu'ici sous la
 * forme d'un `MULT_COMMENTAIRES_F = 1,5` et d'un `coutCommentaires()` qui multipliaient le
 * **coût forfaitaire de SM** de `lire_commentaires`. C'était un transitoire assumé : faute de
 * file, le trait n'avait pas d'autre endroit où mordre.
 *
 * Il en a un depuis que le Fil est **branché** : `MULT_TOXIQUES_F` (`engine/fil.ts`), qui pèse
 * sur le **tirage** — la fréquence des `troll` et des `toxique`, jamais leur prix unitaire.
 * Les deux ne pouvaient pas coexister : garder celui-ci aurait fait payer le trait **deux
 * fois** (plus de toxiques **et** chacun 50 % plus cher), et le déséquilibre aurait été
 * **invisible** — deux lignes, deux fichiers, chacune parfaitement justifiable seule. La règle
 * de non-cumul de SPEC-FIL §7 exigeait le retrait **dans le commit du branchement** ; il y est.
 *
 * ⚠️ **Conséquence à connaître tant que `meta.fil_actif` vaut `false`** (M3.23) : la créatrice
 * garde son avantage (Fidélité ×1,2) et n'a plus son coût, puisque `MULT_TOXIQUES_F` est inerte
 * sans arrivée de commentaires. La cible d'⟦A-4 §2⟧ — *aucun genre objectivement plus fort* —
 * n'est donc **pas** tenue pendant cette fenêtre, et c'est un écart **choisi et daté**, pas une
 * dérive : le jour de l'allumage, le trait reprend sa morsure à sa vraie place.
 *
 * Rien à déclarer ici : ce commentaire **est** la trace du retrait, et il tient la place pour
 * que personne ne réinvente la constante en croyant combler un oubli.
 */
/**
 * Applique le trait de jauge au delta demandé. **Ne touche que les gains** (règle 1).
 *
 * `hors_sommeil` distingue la remontée de SM d'une thérapie de celle d'une nuit : seule la
 * première est amortie (règle 2). Le sommeil appelle donc cette fonction avec `false`, ou ne
 * l'appelle pas du tout.
 */
export function appliquerTraitJauge(genre, jauge, delta, options = {}) {
    if (genre === null || delta <= 0)
        return delta;
    if (genre === "f" && jauge === "loyalty")
        return delta * MULT_FIDELITE_F;
    if (genre === "h" && jauge === "reputation")
        return delta * MULT_REPUTATION_H;
    if (genre === "h" && jauge === "mental" && options.hors_sommeil === true) {
        return delta * MULT_SM_HORS_SOMMEIL_H;
    }
    return delta;
}
/** Le genre porté par l'état — raccourci de lecture pour les appelants. */
export function genreDe(state) {
    return state.genre;
}
//# sourceMappingURL=traits.js.map